import React, { useCallback, useId, useState } from 'react';
import * as z from 'zod';
import { Controller, SubmitHandler, useForm, type UseFormHandleSubmit } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  DialogContent,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
} from '@mui/material';
import Input from 'components/Input';
import InputLabel from 'components/InputLabel';
import RoundedMultilineInput from 'components/RoundedMultilineInput';
import { useTranslation } from 'react-i18next';
import Select from 'components/Select';
import Icon from 'components/Icon';
import PickersTextField from 'components/PickersTextField';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import dayjs, { type Dayjs } from 'dayjs';
import { quizSettingsSchema } from 'features/quizzes/validation/quizSchema';
import { POLLS_INPUT_MAX_LENGTH } from 'features/polls/constants';

const secondsToDayjs = (totalSeconds: number): Dayjs | null => {
  if (totalSeconds === 0) {
    return null;
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return dayjs().hour(hours).minute(minutes).second(seconds);
};

const dayjsToSeconds = (value: Dayjs | null): number => {
  if (!value || !value.isValid()) {
    return 0;
  }
  return value.hour() * 3600 + value.minute() * 60 + value.second();
};

interface DurationFieldProps {
  seconds: number;
  onChangeSeconds: (seconds: number) => void;
  inputRef: React.Ref<HTMLInputElement>;
  error: boolean;
  name: string;
  onBlur: () => void;
}

const DurationField = ({
  seconds,
  onChangeSeconds,
  inputRef,
  error,
  name,
  onBlur,
}: DurationFieldProps) => {
  const [localValue, setLocalValue] = useState<Dayjs | null>(() => secondsToDayjs(seconds));

  const handleChange = useCallback(
    (newValue: Dayjs | null) => {
      setLocalValue(newValue);
      onChangeSeconds(dayjsToSeconds(newValue));
    },
    [onChangeSeconds]
  );

  return (
    <TimeField
      name={name}
      onBlur={onBlur}
      value={localValue}
      onChange={handleChange}
      inputRef={inputRef}
      format="HH:mm:ss"
      ampm={false}
      error={error}
      slots={{
        textField: PickersTextField,
      }}
      slotProps={{
        textField: {
          sx: { flex: 1 },
        },
      }}
    />
  );
};

export type QuizSettingsFormOutput = z.infer<typeof quizSettingsSchema>;
export type QuizSettingsFormInput = z.input<typeof quizSettingsSchema>;

interface QuizSettingsFormProps {
  defaultValues: QuizSettingsFormInput;
  hasQuestions: boolean;
  onSubmit: SubmitHandler<QuizSettingsFormOutput>;
  onNext: SubmitHandler<QuizSettingsFormOutput>;
  renderDialogActions: (state: {
    isValid: boolean;
    handleSubmit: UseFormHandleSubmit<QuizSettingsFormInput, QuizSettingsFormOutput>;
  }) => React.ReactNode;
}

const QuizSettingsForm = ({
  defaultValues,
  hasQuestions,
  onSubmit,
  onNext,
  renderDialogActions,
}: QuizSettingsFormProps) => {
  const { t } = useTranslation('quizzes');
  const formId = useId();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({
    resolver: zodResolver(quizSettingsSchema),
    mode: 'onChange',
    defaultValues,
  });

  return (
    <>
      <DialogContent
        sx={{
          pt: 0,
        }}
      >
        <Stack spacing={6}>
          <FormControl fullWidth error={!!errors?.title}>
            <InputLabel htmlFor="quiz-form-title">{t('quizzes:quiz_form.title.label')}</InputLabel>
            <Controller
              name="title"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <RoundedMultilineInput
                  id="quiz-form-title"
                  autoFocus
                  inputProps={{
                    maxLength: POLLS_INPUT_MAX_LENGTH,
                  }}
                  {...field}
                  inputRef={ref}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('quizzes:quiz_form.time_limit.label')}</InputLabel>
            <Stack direction="row" spacing={2} sx={{ mt: 7 }}>
              <Controller
                name="timingMode"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <Select {...field} inputRef={ref} sx={{ flex: 1 }}>
                    <MenuItem value="quiz">
                      {t('quizzes:quiz_form.time_limit.timing_mode_quiz')}
                    </MenuItem>
                    <MenuItem value="question">
                      {t('quizzes:quiz_form.time_limit.timing_mode_question')}
                    </MenuItem>
                  </Select>
                )}
              />
              <Controller
                name="timeLimitSeconds"
                control={control}
                render={({ field: { value, onChange, ref, ...field } }) => (
                  <DurationField
                    {...field}
                    seconds={value as number}
                    onChangeSeconds={onChange}
                    inputRef={ref}
                    error={!!errors?.timeLimitSeconds}
                  />
                )}
              />
            </Stack>
          </FormControl>

          <FormControl fullWidth error={!!errors?.passingScorePercentage}>
            <InputLabel htmlFor={`${formId}-passing-score`}>
              {t('quizzes:quiz_form.passing_score.label')}
            </InputLabel>
            <Controller
              name="passingScorePercentage"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <Input
                  {...field}
                  id={`${formId}-passing-score`}
                  inputRef={ref}
                  sx={{ width: 'calc(50% - 4px)' }}
                />
              )}
            />
            <FormHelperText>{t('quizzes:quiz_form.passing_score.helper_text')}</FormHelperText>
          </FormControl>

          {/*<SettingControl*/}
          {/*  label={t('quiz_form.show_correct_answers.label')}*/}
          {/*  alignItems="flex-start"*/}
          {/*  helperText={t('quiz_form.show_correct_answers.help_text')}*/}
          {/*  control={*/}
          {/*    <Controller*/}
          {/*      name="showCorrectAnswers"*/}
          {/*      control={control}*/}
          {/*      render={({ field: { value, ref, ...field } }) => (*/}
          {/*        <Switch*/}
          {/*          {...field}*/}
          {/*          slotProps={{*/}
          {/*            input: {*/}
          {/*              ref,*/}
          {/*              'aria-label': value*/}
          {/*                ? t('quiz_form.show_correct_answers.switch_disable_aria_label')*/}
          {/*                : t('quiz_form.show_correct_answers.switch_enable_aria_label'),*/}
          {/*            },*/}
          {/*          }}*/}
          {/*          checked={value}*/}
          {/*        />*/}
          {/*      )}*/}
          {/*    />*/}
          {/*  }*/}
          {/*/>*/}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              endIcon={hasQuestions && <Icon name="chevron-right" />}
              variant="outlined"
              color={hasQuestions ? 'grayscale' : 'primary'}
              onClick={handleSubmit((data) => (hasQuestions ? onNext(data) : onSubmit(data)))}
              disabled={!isValid}
            >
              {hasQuestions ? t('quiz_form.btn_next_question') : t('quiz_form.btn_add_question')}
            </Button>
          </Box>
        </Stack>
      </DialogContent>

      {renderDialogActions &&
        renderDialogActions({
          isValid,
          handleSubmit,
        })}
    </>
  );
};

export default QuizSettingsForm;
